import type { DocumentProjectDetail, DocumentProjectSummary, VersionVerificationResult } from '../types';
import type { DocumentVersionSource, ProjectClauseFlagsResponse } from '@corner/shared';

function headers(getToken: () => string | null): HeadersInit {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function jsonFetch<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(await res.text().catch(() => `Request failed: ${res.status}`));
  return res.json() as Promise<T>;
}

export async function listProjects(getToken: () => string | null): Promise<DocumentProjectSummary[]> {
  const data = await jsonFetch<{ projects: DocumentProjectSummary[] }>('/api/projects', {
    headers: headers(getToken),
  });
  return data.projects;
}

export async function getProject(projectId: string, getToken: () => string | null): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}`, {
    headers: headers(getToken),
  });
  return data.project;
}

export async function getProjectByFile(fileId: string, getToken: () => string | null): Promise<DocumentProjectDetail | null> {
  const res = await fetch(`/api/projects/by-file/${encodeURIComponent(fileId)}`, {
    headers: headers(getToken),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await res.text().catch(() => `Request failed: ${res.status}`));
  const data = await res.json() as { project: DocumentProjectDetail };
  return data.project;
}

export async function moveProjectToFolder(
  projectId: string,
  folderId: string | null,
  getToken: () => string | null,
): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}`, {
    method: 'PATCH',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ folderId }),
  });
  return data.project;
}

/**
 * Rename a project. Trims the new title client-side; the server
 * enforces a 1-200 character non-empty constraint and returns the
 * full updated `DocumentProjectDetail` so callers can swap their
 * cached project state without an extra fetch.
 */
export async function renameProject(
  projectId: string,
  title: string,
  getToken: () => string | null,
): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}`, {
    method: 'PATCH',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title: title.trim() }),
  });
  return data.project;
}

export function folderDownloadUrl(folderId: string): string {
  return `/api/folders/${folderId}/download.zip`;
}

export function fileDocxExportUrl(fileId: string): string {
  return `/api/file/${fileId}/export.docx`;
}

export async function ensureProject(
  input: {
    conversationId: string;
    title?: string;
    folderId?: string | null;
    file?: {
      fileId: string;
      fileName: string;
      mimeType: string;
    } | null;
    source?: DocumentVersionSource;
    changeSummary?: string | null;
  },
  getToken: () => string | null,
): Promise<DocumentProjectSummary> {
  const data = await jsonFetch<{ project: DocumentProjectSummary }>('/api/projects/ensure', {
    method: 'POST',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  return data.project;
}

export async function uploadProjectSeedFile(
  file: File,
  getToken: () => string | null,
): Promise<{ fileId: string; fileName: string; mimeType: string; sizeBytes: number; downloadUrl: string }> {
  const form = new FormData();
  form.append('files', file);
  const data = await jsonFetch<{ file: { fileId: string; fileName: string; mimeType: string; sizeBytes: number; downloadUrl: string } }>('/api/projects/upload-seed', {
    method: 'POST',
    headers: headers(getToken),
    body: form,
  });
  return data.file;
}

export async function createProjectVersion(
  projectId: string,
  input: {
    fileId: string;
    fileName: string;
    mimeType: string;
    source?: DocumentVersionSource;
    changeSummary?: string | null;
    conversationId?: string | null;
    createdFromVersionId?: string | null;
    lineageKind?: 'version' | 'copy';
  },
  getToken: () => string | null,
): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}/versions`, {
    method: 'POST',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  return data.project;
}

export async function createDraftHtmlCopies(
  projectId: string,
  input: {
    draftHtml: string;
    baseTitle: string;
    count: number;
    conversationId?: string | null;
  },
  getToken: () => string | null,
): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}/copies-from-draft`, {
    method: 'POST',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  return data.project;
}

export async function bulkReviseProjectHtml(
  projectId: string,
  input: {
    prompt: string;
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
    conversationId?: string | null;
  },
  getToken: () => string | null,
): Promise<DocumentProjectDetail> {
  const data = await jsonFetch<{ project: DocumentProjectDetail }>(`/api/projects/${projectId}/bulk-revise-html`, {
    method: 'POST',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  return data.project;
}

/**
 * Lists open clause flags across the project's document versions.
 *
 * Backs the project workspace Issues tab. Returns groups already
 * sorted (newest version first; flags within a group sorted by
 * severity), so the panel can render straight from the response with
 * no client-side post-processing.
 */
export async function listProjectClauseFlags(
  projectId: string,
  getToken: () => string | null,
): Promise<ProjectClauseFlagsResponse> {
  return jsonFetch<ProjectClauseFlagsResponse>(`/api/projects/${projectId}/clause-flags?status=pending`, {
    headers: headers(getToken),
  });
}

export async function verifyVersion(
  projectId: string,
  baseVersionId: string,
  targetVersionId: string | null,
  getToken: () => string | null,
): Promise<VersionVerificationResult> {
  const data = await jsonFetch<{ result: VersionVerificationResult }>(`/api/projects/${projectId}/versions/${baseVersionId}/verify`, {
    method: 'POST',
    headers: {
      ...headers(getToken),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ targetVersionId }),
  });
  return data.result;
}
